const ALIYUN_DEVICE_RISK_APP_KEY = 'sh252f7947hscea36efa34217bec06b5'
const ALIYUN_DEVICE_RISK_APP_NAME = 'ruiming_shop_erp'
const ALIYUN_DEVICE_RISK_ENDPOINT = 'https://cloudauth-device.aliyuncs.com'
const ALIYUN_DEVICE_RISK_SDK_ID = 'aliyun-device-risk-sdk'
const TOKEN_COLLECTION_DELAY = 2000
const SDK_READY_TIMEOUT = 5000

interface AliyunDeviceRiskModule {
  init: (options: {
    appKey: string
    appName: string
    endpoints: string[]
  }) => void
  getToken: (bizId?: string) => string
}

interface AliyunDeviceRiskLoader {
  use: (
    moduleName: 'um',
    callback: (state: string, deviceRiskModule: AliyunDeviceRiskModule) => void,
  ) => void
}

declare global {
  interface Window {
    ALIYUN_FP?: AliyunDeviceRiskLoader
  }
}

let sdkReadyPromise: Promise<AliyunDeviceRiskModule | null> | null = null
let initializedAt = 0

function waitForSdkLoader(): Promise<AliyunDeviceRiskLoader | null> {
  if (window.ALIYUN_FP) {
    return Promise.resolve(window.ALIYUN_FP)
  }

  const sdkScript = document.getElementById(ALIYUN_DEVICE_RISK_SDK_ID)
  if (!sdkScript) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    sdkScript.addEventListener('load', () => resolve(window.ALIYUN_FP ?? null), { once: true })
    sdkScript.addEventListener('error', () => resolve(null), { once: true })
  })
}

/**
 * 预热阿里云设备风控 SDK。该过程不会阻塞应用渲染。
 */
export function initAliyunDeviceRisk() {
  if (sdkReadyPromise) {
    return sdkReadyPromise
  }

  sdkReadyPromise = waitForSdkLoader().then(loader => new Promise((resolve) => {
    if (!loader) {
      resolve(null)
      return
    }

    loader.use('um', (state, deviceRiskModule) => {
      if (state !== 'loaded' || !deviceRiskModule) {
        resolve(null)
        return
      }

      try {
        deviceRiskModule.init({
          appKey: ALIYUN_DEVICE_RISK_APP_KEY,
          appName: ALIYUN_DEVICE_RISK_APP_NAME,
          endpoints: [ALIYUN_DEVICE_RISK_ENDPOINT],
        })
        initializedAt = Date.now()
        resolve(deviceRiskModule)
      }
      catch (error) {
        console.error('阿里云设备风控 SDK 初始化失败', error)
        resolve(null)
      }
    })
  }))

  return sdkReadyPromise
}

function timeout(ms: number) {
  return new Promise<null>((resolve) => {
    window.setTimeout(() => resolve(null), ms)
  })
}

/**
 * 获取当前设备 Token。SDK 异常或超时时返回空字符串，避免阻断正常登录。
 */
export async function getAliyunDeviceToken() {
  const deviceRiskModule = await Promise.race([
    initAliyunDeviceRisk(),
    timeout(SDK_READY_TIMEOUT),
  ])

  if (!deviceRiskModule) {
    console.warn('阿里云设备风控 SDK 未就绪，本次登录将使用空 device_token')
    return ''
  }

  const remainingDelay = TOKEN_COLLECTION_DELAY - (Date.now() - initializedAt)
  if (remainingDelay > 0) {
    await new Promise(resolve => window.setTimeout(resolve, remainingDelay))
  }

  try {
    return deviceRiskModule.getToken() || ''
  }
  catch (error) {
    console.error('阿里云设备风控 device_token 获取失败', error)
    return ''
  }
}
